import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SalesAgentState } from "../../../types";
import { queryParams } from "../../../../utils/interface";
import { SalesAgent, UnassignedClient } from "../../../../utils/types";
import { axiosObj } from "../../../../config/apiClients";

const initialState: SalesAgentState = {
  isLoading: false,
  agent: null,
  agents: [],
  unassignedClients: [],
  assignedClients: [],
  transactionStats: [],
  salesAgentsEquityDetails: [],
  unassignedClientsPagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
  assignedClientsPagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
  equityDetailsPagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
};

export const getAllAgents = createAsyncThunk(
  "/sales-agents/getAll",
  async (
    {
      paramPage,
      paramLimit,
      search,
      startDate,
      endDate
    }: queryParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `/sales-agents/getAll?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Sale Agents" });
    }
  },
);

export const getAgent = createAsyncThunk(
  "/sales-agents/getById",
  async (agentId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post("/sales-agents/getById", { agentId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Sale Agent" });
    }
  },
);

export const createAgent = createAsyncThunk(
  "/sales-agents/create",
  async (agent: SalesAgent, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post("/sales-agents/create", agent);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Add Sale Agent" });
    }
  },
);

export const updateAgent = createAsyncThunk(
  "/sales-agents/update",
  async (agent: SalesAgent, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch("/sales-agents/update", agent);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Update Sale Agent" });
    }
  },
);

export const deleteAgent = createAsyncThunk(
  "/sales-agents/delete",
  async (agentId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete("/sales-agents/delete", {
        data: { agentId },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Delete Sale Agent" });
    }
  },
);

export const getDepositedUnassignedClients = createAsyncThunk(
  "/sales-agents/depositedUnassignedClients",
  async (
    {
      paramPage,
      paramLimit,
      search,
    }: { paramPage: number; paramLimit: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `/user/deposited/unassigned?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to fetch unassigned clients" });
    }
  },
);

export const assignClientsToAgent = createAsyncThunk(
  "/sales-agents/assignClients",
  async (
    { agentId, clientIds }: { agentId: string; clientIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.post(`/sales-agents/${agentId}/assignClients`, {
        clientIds,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to assign clients to agent" });
    }
  },
);

export const getAssignedClients = createAsyncThunk(
  "/sales-agents/assignedClients",
  async (
    {
      agentId,
      paramPage,
      paramLimit,
      search,
    }: { agentId: string; paramPage: number; paramLimit: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `/sales-agents/${agentId}/assignedClients?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to fetch assigned clients" });
    }
  },
);

export const unassignClientFromAgent = createAsyncThunk(
  "/sales-agents/unassignClient",
  async (
    { agentId, clientId }: { agentId: string; clientId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.patch(`/sales-agents/${agentId}/unassignClient`, {
        agentId,
        clientId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to unassign client" });
    }
  },
);

export const getAgentTransactionStats = createAsyncThunk(
  "/sales-agents/getAgentTransactionStats",
  async ({ agentId, startDate, endDate }: queryParams, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      if (startDate) params.append("startDate", startDate as string);
      if (endDate) params.append("endDate", endDate as string);

      const response = await axiosObj.get(
        `/sales-agents/${agentId}/stats?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to Fetch Transaction Stats" }
      );
    }
  }
);

export const getAgentEquityDetails = createAsyncThunk(
  "/sales-agents/equityDetails",
  async (
    {
      agentId,
      search,
      paramPage,
      paramLimit,
    }: { agentId: string; search?: string; paramPage: number; paramLimit: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `/sales-agents/${agentId}/equity-details?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to Fetch Agent Equity Details" }
      );
    }
  }
);


const salesAgentSlice = createSlice({
  name: "salesAgent",
  initialState,
  reducers: {
    removeAgent: (state, action) => {
      state.agents = state.agents.filter((a) => a._id !== action.payload);
    },
    setAgent: (state, action) => {
      state.agent = action.payload;
    },
    clearSelectedAgent: (state) => {
      state.agent = null;
      state.isLoading = false;
    },
    resetAgents(state) {
      state.agents = [];
      state.pagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
    resetUnassignedClients(state) {
      state.unassignedClients = [];
      state.unassignedClientsPagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
    resetAssignedClients(state) {
      state.assignedClients = [];
      state.assignedClientsPagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
    resetAgentEquityDetails(state) {
      state.salesAgentsEquityDetails = [];
      state.equityDetailsPagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
    removeAssignedClient(state, action) {
      const { clientId, agentId } = action.payload;
      state.assignedClients = state.assignedClients.filter(
        (c) => c._id !== clientId,
      );
      state.assignedClientsPagination.totalItems = Math.max(
        0,
        state.assignedClientsPagination.totalItems - 1,
      );
      const agent = state.agents.find((a) => a._id === agentId);
      if (agent) {
        if (agent.userCount != null) {
          agent.userCount = Math.max(0, agent.userCount - 1);
        } else if (agent.assignedUsersCount != null) {
          agent.assignedUsersCount = Math.max(0, agent.assignedUsersCount - 1);
        }
      }
    },
    incrementAgentAssignedCount(state, action) {
      const { agentId, count } = action.payload;
      const agent = state.agents.find((a) => a._id === agentId);
      if (agent) {
        if (agent.userCount != null) {
          agent.userCount += count;
        } else {
          agent.assignedUsersCount = (agent.assignedUsersCount ?? 0) + count;
        }
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAllAgents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload.payload.allAgents.map((item: SalesAgent) => ({
          ...item,
          totalNet:
            (item.todayNet?.money ?? 0) +
            ((item.todayNet?.silver ?? 0) + (item.todayNet?.gold ?? 0)),
        }));
        state.pagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage,
        };
      })
      .addCase(getAllAgents.rejected, (state) => {
        state.isLoading = false;
        state.agents = [];
      })
      .addCase(getAgent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agent = action.payload.payload;
      })
      .addCase(getAgent.rejected, (state) => {
        state.isLoading = false;
        state.agent = null;
      })
      .addCase(createAgent.pending, (state) => { state.isLoading = true; })
      .addCase(createAgent.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createAgent.rejected, (state) => { state.isLoading = false; })
      .addCase(updateAgent.pending, (state) => { state.isLoading = true; })
      .addCase(updateAgent.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateAgent.rejected, (state) => { state.isLoading = false; })
      .addCase(deleteAgent.pending, (state) => { state.isLoading = true; })
      .addCase(deleteAgent.fulfilled, (state) => { state.isLoading = false; })
      .addCase(deleteAgent.rejected, (state) => { state.isLoading = false; })
      .addCase(getDepositedUnassignedClients.fulfilled, (state, action) => {
        const payload = action.payload.payload;
        const incoming = payload ?? [];
        const existingIds = new Set(state.unassignedClients.map((c) => c._id));
        state.unassignedClients = [
          ...state.unassignedClients,
          ...incoming.allUsers.filter((c: UnassignedClient) => !existingIds.has(c._id)),
        ];
        state.unassignedClientsPagination = {
          totalItems: payload?.totalItems ?? 0,
          currentPage: payload?.page ?? 1,
          totalPages: payload?.totalPages ?? 1,
          hasNextPage: payload?.hasNextPage ?? false,
        };
      })
      .addCase(assignClientsToAgent.pending, (state) => { state.isLoading = true; })
      .addCase(assignClientsToAgent.fulfilled, (state) => { state.isLoading = false; })
      .addCase(assignClientsToAgent.rejected, (state) => { state.isLoading = false; })
      .addCase(getAssignedClients.fulfilled, (state, action) => {
        const payload = action.payload.payload;
        const incoming = payload?.allUsers ?? [];
        // Page 1 = fresh load (new search), append for page 2+
        if (payload?.page === 1) {
          state.assignedClients = incoming;
        } else {
          const existingIds = new Set(state.assignedClients.map((c) => c._id));
          state.assignedClients = [
            ...state.assignedClients,
            ...incoming.filter((c: UnassignedClient) => !existingIds.has(c._id)),
          ];
        }
        state.assignedClientsPagination = {
          totalItems: payload?.totalItems ?? 0,
          currentPage: payload?.page ?? 1,
          totalPages: payload?.totalPages ?? 1,
          hasNextPage: payload?.hasNextPage ?? false,
        };
      })
      .addCase(getAssignedClients.rejected, () => {
        // optionally set an error flag
      })
      .addCase(unassignClientFromAgent.pending, (state) => { state.isLoading = true; })
      .addCase(unassignClientFromAgent.fulfilled, (state) => { state.isLoading = false; })
      .addCase(unassignClientFromAgent.rejected, (state) => { state.isLoading = false; })
      .addCase(getAgentTransactionStats.pending, (state) => { state.isLoading = true; })
      .addCase(getAgentTransactionStats.fulfilled, (state, action) => { state.isLoading = false; state.transactionStats = action.payload.payload || [] })
      .addCase(getAgentTransactionStats.rejected, (state) => { state.isLoading = false; state.transactionStats = []; })
      .addCase(getAgentEquityDetails.pending, (state) => { state.isLoading = true; })
      .addCase(getAgentEquityDetails.fulfilled, (state, action) => { 
        state.isLoading = false;
        const payload = action.payload.payload;
        state.salesAgentsEquityDetails = payload.agentEquityDetails || [];
        state.equityDetailsPagination = {
          totalItems: payload?.totalItems ?? 0,
          currentPage: payload?.page ?? 1,
          totalPages: payload?.totalPages ?? 1,
          hasNextPage: payload?.hasNextPage ?? false,
        }; 
      })
      .addCase(getAgentEquityDetails.rejected, (state) => { state.isLoading = false; state.salesAgentsEquityDetails = []; });
  },
});

export const {
  setAgent,
  removeAgent,
  resetAgents,
  clearSelectedAgent,
  resetUnassignedClients,
  resetAssignedClients,
  removeAssignedClient,
  incrementAgentAssignedCount,
  resetAgentEquityDetails
} = salesAgentSlice.actions;

export default salesAgentSlice.reducer;